import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Plus } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormBody,
  DashboardPageFormShell,
} from '@shigo-ui/dashboard-ui';
import { Button, Form, Input, Label, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useInitializeFormFromFreshQuery } from '../../../../../lib/useInitializeFormFromFreshQuery';
import { useListsMutations, useRewriteListQuery } from '../../hooks';
import {
  defaultRewriteFormValue,
  emptyRule,
  rewriteFormSchema,
  rewriteListToFormValue,
  serializeRewriteFormValue,
  type RewriteFormValue,
} from '../../rewriteForm';
import { RewriteRuleFields } from './RewriteRuleFields/RewriteRuleFields';

export function RewriteListEditorPage({ listId }: { listId?: string }) {
  const { returnTo } = useReturnNavigation({ to: '/supply/lists/rewrite' });
  const query = useRewriteListQuery(listId);
  const mutations = useListsMutations();
  const pending = mutations.createRewrite.isPending || mutations.updateRewrite.isPending;
  const persistedResult = React.useMemo(
    () => (query.data ? rewriteListToFormValue(query.data) : null),
    [query.data],
  );
  const initialValue = persistedResult?.success ? persistedResult.value : defaultRewriteFormValue;
  const form = useForm<RewriteFormValue>({
    resolver: zodResolver(rewriteFormSchema),
    defaultValues: initialValue,
  });
  const freshDetail = !listId || query.isFetchedAfterMount;
  useInitializeFormFromFreshQuery(form, initialValue, freshDetail, listId);
  const rules = useFieldArray({ control: form.control, name: 'rules' });
  const persistedError = persistedResult?.success === false;
  const loadingDetail = Boolean(listId) && !freshDetail && !query.isError;

  async function save(value: RewriteFormValue) {
    if (persistedError) return;
    try {
      const payload = serializeRewriteFormValue(value);
      if (listId) await mutations.updateRewrite.mutateAsync({ id: listId, payload });
      else await mutations.createRewrite.mutateAsync(payload);
      toast.success(dashboardT(listId ? 'lists.rewrite.updated' : 'lists.rewrite.created'));
      await returnTo({ replace: true });
    } catch (error) {
      showRequestError(error, dashboardT('lists.rewrite.saveError'));
    }
  }

  const title = dashboardT(listId ? 'lists.rewrite.editTitle' : 'lists.rewrite.createTitle');
  return (
    <Form {...form}>
      <DashboardPageFormShell
        title={title}
        backAction={
          pending ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled
              aria-label={dashboardT('lists.back')}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
          ) : (
            <ReturnBackLink
              fallback={{ to: '/supply/lists/rewrite' }}
              label={dashboardT('lists.back')}
            />
          )
        }
      >
        {loadingDetail ? (
          <DashboardPageFormBody width="lg">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-72 w-full" />
          </DashboardPageFormBody>
        ) : (
          <form
            onSubmit={(event) => {
              void form.handleSubmit(save)(event);
            }}
          >
            <DashboardPageFormBody width="lg">
              {query.isError ? (
                <p role="alert" className="text-destructive text-sm">
                  {dashboardT('lists.rewrite.detailError')}
                </p>
              ) : null}
              {persistedError ? (
                <div
                  className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-4 text-sm"
                  role="alert"
                >
                  {dashboardT('lists.rewrite.malformedRules')}
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="rewrite-list-name">
                  {dashboardT('lists.name')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="rewrite-list-name"
                  {...form.register('name')}
                  aria-invalid={Boolean(form.formState.errors.name)}
                />
                {form.formState.errors.name?.message ? (
                  <p role="alert" className="text-destructive text-sm">
                    {form.formState.errors.name.message}
                  </p>
                ) : null}
              </div>
              <section className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-sm font-semibold">{dashboardT('lists.rewrite.rules')}</h2>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      rules.append(emptyRule());
                    }}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {dashboardT('lists.rewrite.addRule')}
                  </Button>
                </div>
                {rules.fields.map((rule, ruleIndex) => (
                  <RewriteRuleFields
                    key={rule.id}
                    ruleIndex={ruleIndex}
                    canRemove={rules.fields.length > 1}
                    onRemove={() => {
                      rules.remove(ruleIndex);
                    }}
                  />
                ))}
              </section>
            </DashboardPageFormBody>
            <DashboardPageFormActions>
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={() => void returnTo()}
              >
                {dashboardT('lists.cancel')}
              </Button>
              <Button type="submit" disabled={pending || persistedError || query.isError}>
                {pending ? dashboardT('lists.saving') : dashboardT('lists.save')}
              </Button>
            </DashboardPageFormActions>
          </form>
        )}
      </DashboardPageFormShell>
    </Form>
  );
}
